import React, { useState } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../config/axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const TeacherDetailsTransfers = ({ teacherId, transfers, onUpdate }) => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    amount: '',
    direction: 'to',
    date: dayjs().format('YYYY-MM-DD'),
    note: ''
  });

  const handleOpenModal = (transfer = null) => {
    if (transfer) {
      setEditingTransfer(transfer);
      setFormData({
        amount: transfer.amount.toString(),
        direction: transfer.direction,
        date: dayjs(transfer.date).format('YYYY-MM-DD'),
        note: transfer.note || ''
      });
    } else {
      setEditingTransfer(null);
      setFormData({
        amount: '',
        direction: 'to',
        date: dayjs().format('YYYY-MM-DD'),
        note: ''
      });
    }
    setError('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTransfer(null);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        direction: formData.direction,
        date: formData.date,
        note: formData.note || undefined
      };

      if (editingTransfer) {
        await apiClient.patch(`/api/teacher/${teacherId}/transfers/${editingTransfer._id}`, payload);
      } else {
        await apiClient.post(`/api/teacher/${teacherId}/transfers`, payload);
      }

      handleCloseModal();
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio del trasferimento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (transferId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo trasferimento?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/teacher/${teacherId}/transfers/${transferId}`);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Errore nell\'eliminazione del trasferimento');
    }
  };

  const formatAmount = (amount, direction) => {
    const sign = direction === 'to' ? '-' : '+';
    return `${sign}€${amount.toFixed(2)}`;
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Trasferimenti
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal()}
            >
              Nuovo Trasferimento
            </Button>
          )}
        </Box>

        {transfers.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Nessun trasferimento registrato
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell align="right"><strong>Importo</strong></TableCell>
                  <TableCell><strong>Direzione</strong></TableCell>
                  <TableCell><strong>Nota</strong></TableCell>
                  <TableCell><strong>Creato da</strong></TableCell>
                  {isAdmin && <TableCell align="right"><strong>Azioni</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow key={transfer._id}>
                    <TableCell>
                      {dayjs(transfer.date).format('DD-MMM-YY')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body1" 
                        fontWeight="medium"
                        color={transfer.direction === 'to' ? 'error.main' : 'success.main'}
                      >
                        {formatAmount(transfer.amount, transfer.direction)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transfer.direction === 'to' ? 'Verso docente' : 'Dal docente'}
                    </TableCell>
                    <TableCell>
                      {transfer.note || '-'}
                    </TableCell>
                    <TableCell>
                      {transfer.createdByUser 
                        ? `${transfer.createdByUser.name} ${transfer.createdByUser.surname}`
                        : '-'}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(transfer)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(transfer._id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modal per creare/modificare transfer */}
      <Dialog 
        open={modalOpen} 
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {editingTransfer ? 'Modifica Trasferimento' : 'Nuovo Trasferimento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Importo (€)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{
                inputProps: { min: 0, step: 0.01 }
              }}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Direzione</InputLabel>
              <Select
                value={formData.direction}
                label="Direzione"
                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
              >
                <MenuItem value="to">Verso docente</MenuItem>
                <MenuItem value="from">Dal docente</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              required
            />
            <TextField
              label="Nota"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.amount || !formData.direction || !formData.date}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeacherDetailsTransfers;
